import { api } from './api';
import { disableNativePush, enableNativePush, ensureNativePushRegisteredIfGranted, getNativePushEnabled, isNativePushPlatform } from './nativePush';

type VapidResp = { configured: boolean; public_key: string | null };

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export function isPushSupported(): boolean {
  if (isNativePushPlatform()) return true;
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
}

export async function getVapidPublicKey(): Promise<string | null> {
  const resp = await api.get<VapidResp>('/api/push/vapid-public-key');
  if (!resp.data?.configured) return null;
  return resp.data.public_key || null;
}

export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register('/push-sw.js');
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (isNativePushPlatform()) return null;
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function enablePush(): Promise<{ ok: boolean; detail?: string }> {
  if (isNativePushPlatform()) return enableNativePush();
  if (!isPushSupported()) return { ok: false, detail: 'Browser-ul nu suporta Push.' };
  const vapid = await getVapidPublicKey();
  if (!vapid) return { ok: false, detail: 'Push nu este configurat pe server (VAPID lipseste).' };

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, detail: 'Permisiune refuzata.' };

  const reg = await registerPushServiceWorker();
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    }));

  const json = sub.toJSON() as any;
  await api.post('/api/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
  return { ok: true };
}

export async function disablePush(): Promise<{ ok: boolean; detail?: string }> {
  if (isNativePushPlatform()) return disableNativePush();
  if (!isPushSupported()) return { ok: true };
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const json = sub.toJSON() as any;
  try {
    await api.post('/api/push/unsubscribe', { endpoint: json.endpoint });
  } catch {
    // best-effort
  }
  await sub.unsubscribe();
  return { ok: true };
}

export async function ensurePushRegisteredIfGranted(): Promise<void> {
  if (isNativePushPlatform()) return ensureNativePushRegisteredIfGranted();
  if (!isPushSupported()) return;
  if (Notification.permission !== 'granted') return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const json = sub.toJSON() as any;
  if (!json?.endpoint || !json?.keys?.p256dh || !json?.keys?.auth) return;
  try {
    await api.post('/api/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
  } catch {
    // ignore
  }
}

export async function getPushEnabledState(): Promise<boolean> {
  if (isNativePushPlatform()) return getNativePushEnabled();
  if (!isPushSupported()) return false;
  const sub = await getExistingSubscription();
  return Boolean(sub && Notification.permission === 'granted');
}
